import confetti from 'canvas-confetti';

/**
 * Generate a random integer between min and max (inclusive).
 * Both bounds are rounded toward the interior of the range
 * (min is ceiled, max is floored) before sampling.
 *
 * @param min - The lower bound (ceiled to the nearest integer).
 * @param max - The upper bound (floored to the nearest integer).
 * @returns A random integer in the range [⌈min⌉, ⌊max⌋].
 */
const random = (min: number, max: number): number => {
  const ceiledMin = Math.ceil(min);
  const flooredMax = Math.floor(max);
  return Math.floor(Math.random() * (flooredMax - ceiledMin + 1)) + ceiledMin;
};

interface ConfettiOptions {
  x: number;
  y: number;
  angle?: number;
  spread?: number;
  particleCount?: number;
}

/**
 * Fire a burst of confetti from a given origin point on the page.
 *
 * Any visual parameters that are not provided will be filled with
 * random values within sensible default ranges. The animation is
 * automatically disabled when the user has enabled the
 * "prefers-reduced-motion" OS setting.
 *
 * @param options - Configuration for the confetti burst.
 * @param options.x - Horizontal origin, expressed as a ratio of the
 *   page width (0 = left edge, 1 = right edge).
 * @param options.y - Vertical origin, expressed as a ratio of the
 *   page height (0 = top edge, 1 = bottom edge).
 * @param options.angle - Launch angle in degrees (defaults to a
 *   random value between 55 and 125).
 * @param options.spread - Spread in degrees (defaults to a random
 *   value between 50 and 70).
 * @param options.particleCount - Number of confetti particles
 *   (defaults to a random value between 40 and 75).
 */
export const fireConfetti = ({
  x,
  y,
  angle,
  spread,
  particleCount,
}: ConfettiOptions): void => {
  confetti({
    angle: angle ?? random(55, 125),
    spread: spread ?? random(50, 70),
    particleCount: particleCount ?? random(40, 75),
    origin: {
      x,
      y,
    },
    disableForReducedMotion: true,
  });
};

export const resetConfetti = confetti.reset;
